import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SessionDetailPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && sessionId) {
      fetchSession();
    }
  }, [user, sessionId]);

  const fetchSession = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSession(response.data);
    } catch (error) {
      console.error('Failed to fetch session:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/progress`,
        {
          session_id: sessionId,
          completed: true,
          rating: rating || null,
          notes: notes || null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setCompleted(true);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      console.error('Failed to save progress:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mentality-300"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600 mb-4">Session not found</p>
            <Link to="/sessions">
              <Button className="bg-gradient-to-r from-mentality-300 to-mentality-500 text-white">Back to Sessions</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-mentality-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <img 
                src="/mentality-logo.jpg" 
                alt="Mentality Logo" 
                className="w-10 h-10 md:w-12 md:h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-mentality-300 to-mentality-600 bg-clip-text text-transparent">
                  Mentality
                </h1>
                <p className="text-xs text-gray-500">The College Mentality</p>
              </div>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">Dashboard</Button>
              </Link>
              <Link to="/sessions">
                <Button variant="ghost" size="sm">Sessions</Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => logout().then(() => navigate('/'))}
                className="border-mentality-200 hover:bg-mentality-50"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {completed ? (
          <Card className="border-2 border-green-200 shadow-xl">
            <CardContent className="pt-6 text-center">
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Great Job!
              </h2>
              <p className="text-gray-600 mb-4">
                You've completed this session. Keep up the great work!
              </p>
              <Link to="/dashboard">
                <Button className="bg-gradient-to-r from-mentality-300 to-mentality-500 hover:from-mentality-400 hover:to-mentality-600 text-white">
                  Back to Dashboard
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Session Info */}
            <div className="lg:col-span-2">
              <Card className="border-2 border-mentality-200 shadow-xl mb-6">
                <div className="h-96 overflow-hidden">
                  <img
                    src={session.image_url}
                    alt={session.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardHeader>
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-mentality-100 text-mentality-700">
                      🎓 {session.category}
                    </Badge>
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline" className="border-mentality-300">{session.difficulty}</Badge>
                      <Badge className="bg-mentality-300 text-white">{session.duration} minutes</Badge>
                    </div>
                  </div>
                  <CardTitle className="text-3xl">{session.title}</CardTitle>
                  <CardDescription className="text-base mt-3">
                    {session.description}
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Session Player */}
              <Card className="border-2 border-mentality-200 shadow-xl">
                <CardHeader>
                  <CardTitle>Session Audio</CardTitle>
                </CardHeader>
                <CardContent>
                  {session.audio_url ? (
                    <div className="space-y-4">
                      <audio 
                        controls 
                        controlsList="nodownload noplaybackrate"
                        className="w-full"
                        preload="metadata"
                        onContextMenu={(e) => e.preventDefault()}
                        style={{
                          width: '100%',
                          height: '54px',
                          borderRadius: '8px'
                        }}
                      >
                        <source src={session.audio_url} type="audio/mpeg" />
                        <source src={session.audio_url} type="audio/mp4" />
                        <source src={session.audio_url} type="audio/x-m4a" />
                        Your browser does not support the audio element.
                      </audio>
                      <div className="bg-gradient-to-br from-mentality-50 to-teal-100 rounded-lg p-6 text-center">
                        <div className="text-4xl mb-3">🎧</div>
                        <p className="text-gray-700 font-medium mb-2">
                          Find a quiet space and use headphones for the best experience
                        </p>
                        <p className="text-sm text-gray-600">
                          Session duration: {session.duration} minutes
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-mentality-50 to-teal-100 rounded-lg p-12 text-center">
                      <div className="text-6xl mb-4">▶️</div>
                      <p className="text-gray-600 mb-4">
                        Audio player would be here
                      </p>
                      <p className="text-sm text-gray-500">
                        Session duration: {session.duration} minutes
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Completion Form */}
            <div className="lg:col-span-1">
              <Card className="border-2 border-mentality-200 shadow-xl sticky top-24">
                <CardHeader>
                  <CardTitle>Complete Session</CardTitle>
                  <CardDescription>
                    Mark this session as complete and share your experience
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Rating */}
                  <div>
                    <Label className="mb-3 block">How was it?</Label>
                    <div className="flex gap-2 justify-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          className="text-4xl transition-transform hover:scale-110"
                        >
                          {star <= rating ? '⭐' : '☆'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label htmlFor="notes" className="mb-2 block">
                      Notes (Optional)
                    </Label>
                    <Textarea
                      id="notes"
                      placeholder="How do you feel? Any insights?"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      className="border-mentality-200 focus:border-mentality-400"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    onClick={handleComplete}
                    disabled={saving}
                    className="w-full bg-gradient-to-r from-mentality-300 to-mentality-500 hover:from-mentality-400 hover:to-mentality-600 text-white"
                    size="lg"
                  >
                    {saving ? 'Saving...' : 'Mark as Complete'}
                  </Button>

                  <Link to="/sessions">
                    <Button variant="outline" className="w-full" size="lg">
                      Back to Sessions
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SessionDetailPage;
